import { useCallback, useEffect, useRef } from "react";
import sanitizeEditorHtml from '../sanitizeEditorHtml';
import Sortable from "sortablejs";
import { useComposerContext } from "../context/ComposerContext";
import { ROOT_ID } from "../config/presets";
import { makeTextBlock, generateBlockId } from "../blocks/factories";
import { useBlockStyles } from "../context/BlockStylesContext";

export default function useEditorDom() {
  const { setBlockStyles } = useBlockStyles();
  const {
    editorRef,
    sortableRef,
    activeSection,
    setActiveSection,
    setSectionMenuPos,
  } = useComposerContext();
  const savedSelection = useRef(null);
  useEffect(() => {
    const el = editorRef.current;
    const paste = event => { const html = event.clipboardData?.getData('text/html'); if (html) { event.preventDefault(); document.execCommand('insertHTML', false, sanitizeEditorHtml(html)); } };
    el?.addEventListener('paste', paste);
    return () => { el?.removeEventListener('paste', paste); sortableRef.current?.destroy(); sortableRef.current = null; };
  }, [editorRef, sortableRef]);
  useEffect(() => {
    const remember = () => {
      const selection = window.getSelection();
      if (selection?.rangeCount && editorRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer)) {
        savedSelection.current = selection.getRangeAt(0).cloneRange();
      }
    };
    document.addEventListener("selectionchange", remember);
    return () => document.removeEventListener("selectionchange", remember);
  }, [editorRef]);

  const getRoot = useCallback(() => {
    const el = editorRef.current;
    if (!el) return null;
    return el.querySelector(`#${ROOT_ID}`);
  }, [editorRef]);

  const initSortable = useCallback(() => {
    const root = getRoot();
    if (!root) return;

    if (sortableRef.current) {
      try {
        sortableRef.current.destroy();
      } catch {}
      sortableRef.current = null;
    }

    sortableRef.current = Sortable.create(root, {
      handle: "[data-bc-drag]",
      draggable: "[data-bc-section]",
      animation: 150,
      ghostClass: "opacity-40",
      dragClass: "opacity-70",
      forceFallback: true,
      fallbackOnBody: true,
      filter: '[data-bc-locked="true"]',
      onStart: () => { root.__bcBeforeDrag = [...root.children]; },
      onEnd: () => {
        const before = root.__bcBeforeDrag || [], after = [...root.children];
        if (before.some((node, i) => node.dataset.bcLocked === 'true' && after[i] !== node)) before.forEach(node => root.appendChild(node));
        delete root.__bcBeforeDrag;
      },
    });
  }, [getRoot, sortableRef]);

  const ensureRoot = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;

    const existing = getRoot();
    if (existing) return;

    const old = el.innerHTML || "";
    el.innerHTML = `<div id="${ROOT_ID}">${old}</div>`;

    initSortable();
  }, [editorRef, getRoot, initSortable]);

  const setEditorHtml = useCallback((html) => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = sanitizeEditorHtml(html);
    // Older drafts had an automatic border; borders are now an explicit choice.
    editorRef.current.querySelectorAll('[data-book-layout]').forEach(card => {
      card.style.border = card.dataset.bookBorder === 'true' ? '1px solid #d8d1c5' : 'none';
    });
    ensureRoot(); initSortable();
  }, [editorRef, ensureRoot, initSortable]);

  const appendBlockToRoot = useCallback((blockHtml) => {
    ensureRoot();
    const root = getRoot();
    if (!root) return;

    root.insertAdjacentHTML("beforeend", blockHtml);
    initSortable();

    const last = root.lastElementChild;
    const focusTarget =
      last?.querySelector("[data-bc-focus]") ||
      last?.querySelector("p, h1, h2, h3, div");

    if (focusTarget) {
      try {
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(focusTarget);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        focusTarget.focus?.();
      } catch {}
    }
  }, [ensureRoot, getRoot, initSortable]);

  const focusEditor = useCallback(() => {
    const live = window.getSelection();
    const range = live?.rangeCount && editorRef.current?.contains(live.getRangeAt(0).commonAncestorContainer)
      ? live.getRangeAt(0).cloneRange() : savedSelection.current;
    editorRef.current?.focus();
    if (range && editorRef.current?.contains(range.commonAncestorContainer)) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [editorRef]);

  const insertBlockSmart = useCallback((blockHtml) => {
    ensureRoot();
    focusEditor();

    const sel = window.getSelection();
    const root = getRoot();
    let insertedEl = null;

    if (sel && sel.rangeCount && root) {
      const range = sel.getRangeAt(0);

      if (root.contains(range.startContainer)) {
        let node = range.startContainer;

        while (node && node !== root && !node.dataset?.bcSection) {
          node = node.parentNode;
        }

        if (node && node.dataset?.bcSection) {
          node.insertAdjacentHTML("afterend", blockHtml);
          insertedEl = node.nextElementSibling;
        }
      }
    }

    if (!insertedEl) {
      appendBlockToRoot(blockHtml);
      insertedEl = getRoot()?.lastElementChild;
    }

    if (insertedEl) {
      let focusTarget =
        insertedEl.querySelector("[data-bc-focus]") ||
        insertedEl.querySelector("[contenteditable='true']") ||
        insertedEl.querySelector("p, h1, h2, h3");

      if (!focusTarget && insertedEl.nextElementSibling) {
        focusTarget =
          insertedEl.nextElementSibling.querySelector("[data-bc-focus]") ||
          insertedEl.nextElementSibling.querySelector("[contenteditable='true']");
      }

      if (focusTarget) {
        setTimeout(() => {
          try {
            const r = document.createRange();
            const s = window.getSelection();
            r.selectNodeContents(focusTarget);
            r.collapse(true);
            s.removeAllRanges();
            s.addRange(r);
            focusTarget.focus();
          } catch {}
        }, 0);
      }
    }
    initSortable();
  }, [ensureRoot, focusEditor, getRoot, appendBlockToRoot, initSortable]);

  const getBodyInnerHtml = useCallback(() => {
    ensureRoot();
    const root = getRoot();
    return root?.innerHTML || "";
  }, [ensureRoot, getRoot]);

  const normalizeEditor = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;

    ensureRoot();
    const root = getRoot();
    if (!root) return;

    const strayNodes = [];
    for (const node of Array.from(el.childNodes)) {
      if (node.nodeType === 1 && node.id === ROOT_ID) continue;
      if (node.nodeType === 3 && !node.textContent.trim()) continue;
      strayNodes.push(node);
    }

    if (strayNodes.length) {
      const temp = document.createElement("div");
      strayNodes.forEach((n) => temp.appendChild(n));

      appendBlockToRoot(makeTextBlock(temp.innerHTML || ""));

      el.innerHTML = `<div id="${ROOT_ID}">${root.innerHTML}</div>`;
    }
    initSortable();
  }, [editorRef, ensureRoot, getRoot, appendBlockToRoot, initSortable]);

  const positionFloatingPanel = useCallback((targetEl, setter, xOffset = 0) => {
    if (!targetEl) return;
    const r = targetEl.getBoundingClientRect();
    setter({ top: r.top + window.scrollY - 42, left: r.right + window.scrollX - 170 + xOffset });
  }, []);

  const openSectionMenu = useCallback((sectionEl) => {
    if (!sectionEl) return;
    setActiveSection(sectionEl);
    positionFloatingPanel(sectionEl, setSectionMenuPos);
  }, [setActiveSection, setSectionMenuPos, positionFloatingPanel]);

  const moveSection = useCallback((direction, section = activeSection) => {
    const activeSection = section;
    if (!activeSection) return;
    if (activeSection.dataset.bcLocked === "true") return;
    const sibling = direction === "up" ? activeSection.previousElementSibling : activeSection.nextElementSibling;
    if (!sibling) return;
    if (sibling.dataset.bcLocked === "true") return;

    if (direction === "up") {
      sibling.insertAdjacentElement("beforebegin", activeSection);
    } else {
      sibling.insertAdjacentElement("afterend", activeSection);
    }

    openSectionMenu(activeSection);
  }, [activeSection, openSectionMenu]);

  const duplicateSection = useCallback((section = activeSection) => {
    const activeSection = section;
    if (!activeSection) return;
    if (activeSection.dataset.bcLocked === "true") {
      alert("Pinned blocks cannot be duplicated.");
      return;
    }
    const clone = activeSection.cloneNode(true);
    const ids = [];
    [clone, ...clone.querySelectorAll("[data-bc-id]")].forEach((el) => {
      const oldId = el.getAttribute("data-bc-id");
      if (!oldId) return;
      const newId = generateBlockId();
      el.setAttribute("data-bc-id", newId);
      ids.push([oldId, newId]);
    });
    setBlockStyles((styles) => {
      const next = { ...styles };
      ids.forEach(([oldId, newId]) => { if (styles[oldId]) next[newId] = { ...styles[oldId] }; });
      return next;
    });
    activeSection.after(clone);
    initSortable();
  }, [activeSection, initSortable, setBlockStyles]);

  const deleteSection = useCallback((section = activeSection) => {
    const activeSection = section;
    if (!activeSection) return;

    if (activeSection.dataset.bcLocked === "true") {
      alert("Header/Footer can't be deleted (edit it from Branding).");
      return;
    }

    activeSection.remove();
    setActiveSection(null);

    const root = getRoot();
    if (root && !root.children.length) appendBlockToRoot(makeTextBlock(""));
  }, [activeSection, setActiveSection, getRoot, appendBlockToRoot]);

  const exec = useCallback((command, value = null) => {
    focusEditor();
    try {
      document.execCommand(command, false, value);
    } catch {}
  }, [focusEditor]);

  const insertHTML = useCallback((html) => {
    focusEditor();
    ensureRoot();

    const sel = window.getSelection?.();
    if (!sel || !sel.rangeCount || !getRoot()?.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      appendBlockToRoot(html);
      return;
    }

    const range = sel.getRangeAt(0);
    range.deleteContents();

    const temp = document.createElement("div");
    temp.innerHTML = html;

    const frag = document.createDocumentFragment();
    let node,
      lastNode = null;

    while ((node = temp.firstChild)) lastNode = frag.appendChild(node);

    range.insertNode(frag);

    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [focusEditor, ensureRoot, appendBlockToRoot, getRoot]);

  const insertLink = useCallback(() => {
    const url = prompt("Link URL:", "https://");
    if (!url) return;
    exec("createLink", url);
  }, [exec]);

  const clearFormatting = useCallback(() => exec("removeFormat"), [exec]);

  return {
    getRoot,
    ensureRoot,
    initSortable,
    setEditorHtml,
    appendBlockToRoot,
    insertBlockSmart,
    getBodyInnerHtml,
    normalizeEditor,
    focusEditor,
    positionFloatingPanel,
    openSectionMenu,
    moveSection,
    duplicateSection,
    deleteSection,
    exec,
    insertHTML,
    insertLink,
    clearFormatting,
  };
}
